import React, { useState } from "react";
import { DashboardEditorProps } from "components/ComponentType";
import DatasetChartConfig from "components/basic/dataset-chart-config/DatasetChartConfig";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DatasetMultiSelect } from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { Divider } from "primereact/divider";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DatasetScatterProps, ImageConfig, YAxisIdentifier } from "../interfaces";
import ChartIdentifiersEditor from "../ChartIdentifiersEditor";

const DatasetScatterChartEditor: React.FC<DashboardEditorProps<DatasetScatterProps>> = props => {
  const { config, onConfigChange } = props;
  const [isIdentifiersEditDialogVisible, setIsIdentifiersEditDialogVisible] =
    useState<boolean>(false);

  const yAxisIdentifierValue = Array.isArray(config?.yAxisIdentifier)
    ? config?.yAxisIdentifier
    : [];

  const handleChange = (
    field: keyof DatasetScatterProps,
    value: string | string[] | number | boolean | null | YAxisIdentifier[]
  ) => {
    if (value === null) {
      const { [field]: remove, ...rest } = config;
      onConfigChange({ ...(rest as DatasetScatterProps) });
      return;
    }
    onConfigChange({ ...config, [field]: value });
  };

  const handleAxisConfigChange = (
    field: "xRange" | "yRange",
    place: "from" | "to",
    value: number | null
  ) => {
    const range = config?.axisConfig?.[field] || [];
    range[place === "from" ? 0 : 1] = value!;
    onConfigChange({ ...config, axisConfig: { ...config?.axisConfig, [field]: range } });
  };

  const handleImageConfigChange = (field: keyof ImageConfig, value: string | number | null) => {
    if ((value === null || value === "") && config.imageConfig) {
      const { [field]: remove, ...rest } = config.imageConfig;
      onConfigChange({ ...config, imageConfig: { ...(rest as ImageConfig) } });
      return;
    }
    onConfigChange({ ...config, imageConfig: { ...config?.imageConfig!, [field]: value } });
  };

  return (
    <div className="grid p-fluid">
      <DatasetChartConfig config={config} onConfigChange={onConfigChange} />
      <InputLabelWrapper labelName="X Axis Identifier">
        <DatasetDropdown
          value={config?.xAxisIdentifier}
          onChange={value => handleChange("xAxisIdentifier", value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Y Axis Identifiers" col={12}>
        <div className="flex flex-row">
          <DatasetMultiSelect
            value={yAxisIdentifierValue.map(item => item.identifier)}
            onChange={selectedIdentifiers => {
              const existingIdentifiersMap = new Map(
                yAxisIdentifierValue.map(item => [item.identifier, item.overriddenTitle])
              );

              const updatedIdentifiers = selectedIdentifiers.map(identifier => ({
                identifier,
                overriddenTitle: existingIdentifiersMap.get(identifier) || "",
              }));

              handleChange("yAxisIdentifier", updatedIdentifiers);
            }}
          />
          <Button
            icon="pi pi-pencil"
            type="button"
            className="p-button-secondary"
            tooltip="Edit Identifiers"
            onClick={() => setIsIdentifiersEditDialogVisible(true)}
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Color Axis Identifier">
        <DatasetDropdown
          value={config?.colorAxisIdentifier}
          onChange={value => handleChange("colorAxisIdentifier", value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Size Axis Identifier">
        <DatasetDropdown
          value={config?.sizeAxisIdentifier}
          onChange={value => handleChange("sizeAxisIdentifier", value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="X Axis Range" col={3}>
        <div className="p-inputgroup">
          <InputNumber
            value={config?.axisConfig?.xRange?.[0]}
            onChange={e => handleAxisConfigChange("xRange", "from", e.value)}
            prefix="from "
          />
          <InputNumber
            value={config?.axisConfig?.xRange?.[1]}
            onChange={e => handleAxisConfigChange("xRange", "to", e.value)}
            prefix="to "
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Y Axis Range" col={3}>
        <div className="p-inputgroup">
          <InputNumber
            value={config?.axisConfig?.yRange?.[0]}
            onChange={e => handleAxisConfigChange("yRange", "from", e.value)}
            prefix="from "
          />
          <InputNumber
            value={config?.axisConfig?.yRange?.[1]}
            onChange={e => handleAxisConfigChange("yRange", "to", e.value)}
            prefix="to "
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Highlight Count" col={2}>
        <InputNumber
          value={config?.highlightCount}
          onChange={e => handleChange("highlightCount", e.value)}
          showButtons
        />
      </InputLabelWrapper>
      <Divider />
      <h6 className="w-full m-0">Image Configuration:</h6>
      <InputLabelWrapper labelName="Image Source">
        <InputText
          value={config?.imageConfig?.source ?? ""}
          onChange={e => handleImageConfigChange("source", e.target.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="X" col={1}>
        <InputNumber
          value={config?.imageConfig?.x}
          onChange={e => handleImageConfigChange("x", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Y" col={1}>
        <InputNumber
          value={config?.imageConfig?.y}
          onChange={e => handleImageConfigChange("y", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="X Size" col={1}>
        <InputNumber
          value={config?.imageConfig?.sizex}
          onChange={e => handleImageConfigChange("sizex", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Y Size" col={1}>
        <InputNumber
          value={config?.imageConfig?.sizey}
          onChange={e => handleImageConfigChange("sizey", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Opacity" col={1}>
        <InputNumber
          value={config?.imageConfig?.opacity}
          onChange={e => handleImageConfigChange("opacity", e.value)}
          maxFractionDigits={2}
        />
      </InputLabelWrapper>
      <Dialog
        onHide={() => setIsIdentifiersEditDialogVisible(false)}
        visible={isIdentifiersEditDialogVisible}
        header="Edit identifiers"
        style={{ width: "min(100vw, 1000px)", height: "400px" }}
      >
        <ChartIdentifiersEditor
          currentConfig={yAxisIdentifierValue}
          setCurrentConfig={identifiers => handleChange("yAxisIdentifier", identifiers)}
        />
      </Dialog>
    </div>
  );
};

export default DatasetScatterChartEditor;
